// D2 — Web Documentation: the full Documentation experience (TechSpec §29,
// Master §12/§29/§30/§31). Rendered as a first-class view inside the
// state-driven ChatPage shell (no URL router — D0 finding #4); deep/direct
// links are document hashes handled by @/lib/docs/deepLink.
//
// Surfaces:
//  * Landing  — sections (from the canonical corpus) + article navigation +
//               a Getting-Started highlight + the search entry.
//  * Article  — breadcrumbs, title, status/version/platform metadata, secure
//               Markdown body, related articles, "copy direct link", back to
//               Documentation.
//
// D4 — search is the canonical backend search (lib/docs/api.ts →
// GET /api/docs/search, USER/web scope). The backend is the security
// boundary: results/counts/snippets are ACL-first server-side (TechSpec
// §21); the context sent (surface/route/section) is a RANKING signal only
// and never grants access. The build-time user corpus (content.ts) is used
// for article RENDERING (public product docs) and as a graceful offline
// fallback when the API is unreachable — it can never expose restricted
// content (only public user/* articles are bundled).
//
// Usable WITHOUT RealityAI Guide (D2-A17). `status: future` articles are
// clearly marked and excluded from search (D2-A9).

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BookOpen,
  Check,
  ChevronRight,
  List,
  Search,
  Smartphone,
  Monitor,
  X,
  Copy,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getArticleBySlug,
  getArticles,
  getSections,
  searchArticles,
  sectionLabel,
} from "@/lib/docs/content";
import { fetchDocArticle, searchDocs } from "@/lib/docs/api";
import type { DocsSearchResult } from "@/lib/docs/api";
import type { DocArticle } from "@/lib/docs/types";
import {
  currentDocsHash,
  docsArticleHash,
  docsLandingHash,
  parseDocsHash,
  setDocsHash,
} from "@/lib/docs/deepLink";
import { DocsMarkdown } from "@/lib/docs/DocsMarkdown";
import { copyText } from "@/components/MarkdownBlock";
import { StatusBadge } from "@/lib/docs/StatusBadge";

interface DocsViewProps {
  /** Return to the Chat view (exit Documentation). */
  onExit: () => void;
}

function platformLabel(art: DocArticle): string | null {
  const p = art.platforms;
  if (p.length === 0) return null;
  if (p.length === 1) {
    return p[0] === "web" ? "Web" : p[0] === "android" ? "Android" : "Web + Android";
  }
  return "Web + Android";
}

function PlatformIcon({ art }: { art: DocArticle }) {
  const hasWeb = art.platforms.includes("web") || art.platforms.includes("web+android");
  const hasAndroid = art.platforms.includes("android") || art.platforms.includes("web+android");
  return (
    <span className="inline-flex items-center gap-1" aria-hidden>
      {hasWeb && <Monitor size={12} />}
      {hasAndroid && <Smartphone size={12} />}
    </span>
  );
}

export function DocsView({ onExit }: DocsViewProps) {
  const articles = useMemo(() => getArticles(), []);
  const sections = useMemo(() => getSections(), []);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const exists = useCallback((slug: string) => getArticleBySlug(slug) !== undefined, []);

  // ---- deep-link <-> state sync ----
  // D4: the hash opens ANY canonical article slug; the LOCAL corpus renders
  // known slugs directly, unknown slugs are arbitrated by the backend
  // (/api/docs/articles/{slug} — 404 for unknown OR not-authorized, so a
  // probed restricted slug simply lands on the landing page: no leak).
  // Initial load: honor the current hash (a direct link / reload re-opens the
  // same article — D2-A7).
  useEffect(() => {
    const parsed = currentDocsHash();
    if (parsed?.view === "article") {
      setActiveSlug(parsed.slug);
    } else {
      setActiveSlug(null);
    }
  }, []);

  // Browser back/forward and external hash edits (popstate/hashchange):
  // re-read the hash. Non-docs hashes fall back to the landing (we never
  // auto-exit the view — exit is an explicit user action via onExit).
  useEffect(() => {
    const onHash = () => {
      const parsed = parseDocsHash(window.location.hash);
      if (parsed?.view === "article") {
        setActiveSlug(parsed.slug);
      } else {
        setActiveSlug(null);
      }
    };
    window.addEventListener("hashchange", onHash);
    window.addEventListener("popstate", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("popstate", onHash);
    };
  }, []);

  const navigateTo = useCallback((slug: string) => {
    setActiveSlug(slug);
    setDocsHash(docsArticleHash(slug));
    setMobileNavOpen(false);
    setQuery("");
  }, []);

  const goLanding = useCallback(() => {
    setActiveSlug(null);
    setDocsHash(docsLandingHash());
    setMobileNavOpen(false);
    setQuery("");
  }, []);

  const localArticle = activeSlug ? getArticleBySlug(activeSlug) : undefined;

  // ---- D4: article body for slugs absent from the local corpus (backend,
  //      ACL-first; 404 for anything the caller may not read — no leak) ----
  const [fetchedArticle, setFetchedArticle] = useState<DocArticle | null>(null);
  useEffect(() => {
    if (!activeSlug || localArticle) {
      setFetchedArticle(null);
      return;
    }
    let alive = true;
    fetchDocArticle(activeSlug)
      .then((a) => {
        if (!alive) return;
        // Normalize the backend row to the local DocArticle view (the Web
        // renderer contract; last_reviewed is optional in the local model).
        setFetchedArticle({
          slug: a.slug,
          title: a.title,
          section: a.section,
          description: a.description,
          status: a.status,
          version: a.version,
          last_reviewed: a.last_reviewed ?? "",
          platforms: a.platforms as DocArticle["platforms"],
          related: a.related,
          body: a.body,
        });
      })
      .catch(() => {
        if (alive) setFetchedArticle(null);
      });
    return () => {
      alive = false;
    };
  }, [activeSlug, localArticle]);

  const activeArticle = localArticle ?? fetchedArticle ?? undefined;

  // ---- D4: canonical backend search (USER/web scope, ACL-first) ----------
  const trimmed = query.trim();
  const [apiResults, setApiResults] = useState<DocsSearchResult[] | null>(null);
  useEffect(() => {
    if (!searchOpen || !trimmed) {
      setApiResults(null);
      return;
    }
    const controller = new AbortController();
    // Debounce keystrokes; abort in-flight requests on the next one.
    const t = window.setTimeout(() => {
      searchDocs(trimmed, {
        platform: "web",
        // UI Context Contract: safe whitelisted fields, RANKING ONLY — the
        // backend role always comes from the authenticated JWT, never here.
        context: { surface: "web" },
        signal: controller.signal,
      })
        .then((res) => setApiResults(res.results))
        .catch(() => {
          // Network/API failure → keep the local public-corpus fallback
          // (user/* articles are not restricted content).
        });
    }, 180);
    return () => {
      window.clearTimeout(t);
      controller.abort();
    };
  }, [trimmed, searchOpen]);

  interface SearchHit {
    slug: string;
    title: string;
    sub: string;
  }

  const searchResults: SearchHit[] = useMemo(() => {
    if (!trimmed) return [];
    if (apiResults !== null) {
      // Canonical backend results (authoritative; ACL already applied).
      return apiResults.map((r) => ({
        slug: r.slug,
        title: r.title,
        sub: `${sectionLabel(r.section)} · ${r.snippet || r.description}`,
      }));
    }
    // Fallback (API unreachable / pre-response window): the D2 client-side
    // filter over the PUBLIC user corpus only.
    return searchArticles(trimmed).map((a) => ({
      slug: a.slug,
      title: a.title,
      sub: `${sectionLabel(a.section)} · ${a.description}`,
    }));
  }, [trimmed, apiResults]);

  const copyLink = useCallback(async () => {
    if (!activeSlug) return;
    const url = `${window.location.origin}${window.location.pathname}${docsArticleHash(activeSlug)}`;
    // Use the shared copyText (MarkdownBlock): it falls back to the hidden
    // textarea + document.execCommand("copy") when navigator.clipboard is
    // UNAVAILABLE — which is the case on plain http:// origins (DEV serves
    // http, not https/localhost), where navigator.clipboard is undefined.
    // Without the fallback the copy silently failed and the button never
    // showed the success state (D2 closeout A8).
    const ok = await copyText(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  }, [activeSlug]);

  return (
    <div className="flex h-full min-h-0 bg-app">
      {/* ===== Sidebar: section navigation (desktop rail + mobile drawer) ===== */}
      {/* Desktop */}
      <aside
        aria-label="Разделы документации"
        className="hidden md:block w-72 shrink-0 border-r border-hairline bg-surface overflow-y-auto"
      >
        <SidebarContent
          sections={sections}
          activeSlug={activeSlug}
          onLanding={goLanding}
          onArticle={navigateTo}
          showLanding={activeSlug === null}
        />
      </aside>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={() => setMobileNavOpen(false)} aria-hidden />
          <aside
            className="absolute inset-y-0 left-0 w-72 bg-surface border-r border-hairline overflow-y-auto"
            aria-label="Разделы документации"
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <span className="text-sm font-medium text-primary">Документация</span>
              <button
                onClick={() => setMobileNavOpen(false)}
                className="w-8 h-8 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary"
                aria-label="Закрыть меню"
              >
                <X size={16} />
              </button>
            </div>
            <SidebarContent
              sections={sections}
              activeSlug={activeSlug}
              onLanding={goLanding}
              onArticle={navigateTo}
              showLanding={activeSlug === null}
            />
          </aside>
        </div>
      )}

      {/* ===== Main column ===== */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        {/* Header bar */}
        <header className="shrink-0 flex items-center gap-2 px-3 md:px-5 h-14 border-b border-hairline bg-app">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary"
            aria-label="Разделы документации"
          >
            <List size={18} />
          </button>
          <button
            onClick={onExit}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm text-secondary hover:text-primary hover:bg-hoverbg transition-colors"
            aria-label="Вернуться к чату"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Назад</span>
          </button>
          <div className="w-px h-5 bg-hairline" aria-hidden />
          <BookOpen size={16} className="text-accent shrink-0" />
          <span className="text-sm font-medium text-primary truncate">Документация</span>

          <div className="flex-1" />

          {/* Search entry (D2 client-side static search; D4 = deterministic
              backend search). Toggles an inline results panel below. */}
          <div className="relative flex items-center">
            <div className="hidden sm:flex items-center gap-2 h-9 pl-3 pr-2 rounded-lg bg-hoverbg border border-hairline focus-within:border-accent transition-colors w-56 focus-within:w-72">
              <Search size={15} className="text-tertiary shrink-0" aria-hidden />
              <input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                placeholder="Поиск в документации…"
                aria-label="Поиск в документации"
                className="flex-1 min-w-0 bg-transparent text-sm text-primary placeholder:text-tertiary focus:outline-none"
              />
            </div>
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="sm:hidden w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary"
              aria-label="Поиск в документации"
            >
              <Search size={18} />
            </button>
          </div>
        </header>

        {/* Search results overlay panel */}
        {searchOpen && (
          <div className="shrink-0 border-b border-hairline bg-surface max-h-[50vh] overflow-y-auto">
            <div className="px-4 md:px-6 py-3">
              <div className="sm:hidden flex items-center gap-2 mb-3">
                <Search size={15} className="text-tertiary" aria-hidden />
                <input
                  type="search"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Поиск в документации…"
                  aria-label="Поиск в документации"
                  className="flex-1 h-9 rounded-lg bg-hoverbg border border-hairline px-3 text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent"
                />
              </div>
              {query.trim() ? (
                searchResults.length > 0 ? (
                  <ul className="space-y-1" role="listbox" aria-label="Результаты поиска">
                    {searchResults.map((hit) => (
                      <li key={hit.slug}>
                        <button
                          onClick={() => navigateTo(hit.slug)}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-hoverbg transition-colors"
                        >
                          <div className="text-sm text-primary truncate">{hit.title}</div>
                          <div className="text-xs text-tertiary truncate">{hit.sub}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-3 py-4 text-sm text-tertiary">
                    По запросу «{query.trim()}» ничего не найдено.
                  </div>
                )
              ) : (
                <div className="px-3 py-4 text-sm text-tertiary">
                  Начните вводить запрос, чтобы найти статью в документации.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Article / Landing scroll area */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {activeArticle ? (
            <ArticleView
              article={activeArticle}
              onNavigate={navigateTo}
              onLanding={goLanding}
              articleExists={exists}
              onCopyLink={copyLink}
              copied={copied}
            />
          ) : (
            <LandingView
              sections={sections}
              onArticle={navigateTo}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar navigation (sections -> articles)
// ---------------------------------------------------------------------------
function SidebarContent({
  sections,
  activeSlug,
  onLanding,
  onArticle,
  showLanding,
}: {
  sections: ReturnType<typeof getSections>;
  activeSlug: string | null;
  onLanding: () => void;
  onArticle: (slug: string) => void;
  showLanding: boolean;
}) {
  return (
    <nav className="py-3 px-2" aria-label="Навигация документации">
      <button
        onClick={onLanding}
        aria-current={showLanding ? "page" : undefined}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors mb-1",
          showLanding ? "bg-hoverbg2 text-primary" : "text-secondary hover:bg-hoverbg hover:text-primary"
        )}
      >
        <BookOpen size={15} className="text-accent" />
        Обзор
      </button>

      <ul className="space-y-3 mt-2">
        {sections.map((sec) => (
          <li key={sec.id}>
            <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-tertiary">
              {sec.label}
            </div>
            <ul className="space-y-0.5">
              {sec.articles.map((a) => (
                <li key={a.slug}>
                  <button
                    onClick={() => onArticle(a.slug)}
                    aria-current={activeSlug === a.slug ? "page" : undefined}
                    className={cn(
                      "w-full text-left flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-md text-[13px] transition-colors",
                      activeSlug === a.slug
                        ? "bg-hoverbg2 text-primary font-medium"
                        : "text-secondary hover:bg-hoverbg hover:text-primary"
                    )}
                  >
                    <span className="flex-1 min-w-0 truncate">{a.title}</span>
                    {a.status === "future" && (
                      <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-accent border border-accent/40 rounded px-1">
                        Скоро
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Landing
// ---------------------------------------------------------------------------
function LandingView({
  sections,
  onArticle,
}: {
  sections: ReturnType<typeof getSections>;
  onArticle: (slug: string) => void;
}) {
  const gettingStarted = sections.find((s) => s.id === "getting-started");

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-medium text-primary">Документация RealityAI</h1>
        <p className="mt-2 text-sm text-secondary max-w-2xl leading-relaxed">
          Справочник по продукту: как пользоваться RealityAI, где находятся
          функции, как решать типовые проблемы. Начните с раздела «Начало
          работы» или найдите нужную статью через поиск.
        </p>
      </div>

      {gettingStarted && (
        <section aria-label="Начало работы" className="mb-8">
          <h2 className="text-lg font-medium text-primary mb-3 flex items-center gap-2">
            <BookOpen size={18} className="text-accent" />
            Начните здесь
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {gettingStarted.articles.map((a) => (
              <ArticleCard key={a.slug} article={a} onClick={() => onArticle(a.slug)} />
            ))}
          </div>
        </section>
      )}

      <section aria-label="Все разделы">
        <h2 className="text-lg font-medium text-primary mb-3">Все разделы</h2>
        <div className="space-y-6">
          {sections
            .filter((s) => s.id !== "getting-started")
            .map((sec) => (
              <div key={sec.id}>
                <h3 className="text-sm font-semibold text-secondary mb-2">{sec.label}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {sec.articles.map((a) => (
                    <ArticleCard key={a.slug} article={a} onClick={() => onArticle(a.slug)} />
                  ))}
                </div>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}

function ArticleCard({ article, onClick }: { article: DocArticle; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="group text-left bg-surface border border-hairline rounded-xl p-4 transition-colors hover:border-strongline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-primary group-hover:text-accent transition-colors line-clamp-2">
          {article.title}
        </span>
        {article.status === "future" && (
          <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-accent border border-accent/40 rounded px-1 mt-0.5">
            Скоро
          </span>
        )}
      </div>
      <p className="mt-1.5 text-xs text-secondary line-clamp-2">{article.description}</p>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Article view
// ---------------------------------------------------------------------------
function ArticleView({
  article,
  onNavigate,
  onLanding,
  articleExists,
  onCopyLink,
  copied,
}: {
  article: DocArticle;
  onNavigate: (slug: string) => void;
  onLanding: () => void;
  articleExists: (slug: string) => boolean;
  onCopyLink: () => void;
  copied: boolean;
}) {
  const platform = platformLabel(article);
  const related = article.related
    .map((s) => getArticleBySlug(s))
    .filter((a): a is DocArticle => a !== undefined);

  return (
    <article className="max-w-3xl mx-auto px-4 md:px-8 py-6 md:py-10" aria-labelledby="docs-article-title">
      {/* Breadcrumbs */}
      <nav aria-label="Хлебные крошки" className="mb-4">
        <ol className="flex flex-wrap items-center gap-1 text-[13px] text-tertiary">
          <li>
            <button
              onClick={onLanding}
              className="hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
            >
              Документация
            </button>
          </li>
          <li aria-hidden>
            <ChevronRight size={13} className="mx-0.5 text-disabled" />
          </li>
          <li>
            <span className="text-secondary">{sectionLabel(article.section)}</span>
          </li>
          <li aria-hidden>
            <ChevronRight size={13} className="mx-0.5 text-disabled" />
          </li>
          <li>
            <span className="text-primary font-medium">{article.title}</span>
          </li>
        </ol>
      </nav>

      {/* Future banner — must not masquerade as current (D2-A9) */}
      {article.status === "future" && (
        <div
          role="status"
          className="mb-5 rounded-lg border border-accent/30 bg-accent/5 px-4 py-3 text-sm text-secondary flex items-start gap-2.5"
        >
          <span className="mt-0.5 text-accent font-medium shrink-0">Скоро</span>
          <span>
            Эта функция ещё не доступна в текущей версии RealityAI. Статья
            описывает её будущее поведение и не является актуальной инструкцией.
          </span>
        </div>
      )}

      {/* Title + metadata */}
      <header className="mb-5">
        <div className="flex items-start justify-between gap-3">
          <h1 id="docs-article-title" className="text-2xl md:text-3xl font-medium text-primary">
            {article.title}
          </h1>
          <button
            onClick={onCopyLink}
            className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs text-secondary hover:text-primary hover:bg-hoverbg transition-colors"
            aria-label="Скопировать прямую ссылку на статью"
          >
            {copied ? <Check size={14} className="text-accent" /> : <Copy size={14} />}
            {copied ? "Скопировано" : "Ссылка"}
          </button>
        </div>

        {article.description && (
          <p className="mt-2 text-sm text-secondary leading-relaxed">{article.description}</p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-tertiary">
          <StatusBadge status={article.status} />
          <span className="inline-flex items-center gap-1.5">
            <span className="text-disabled">·</span>
            Версия {article.version}
          </span>
          {article.last_reviewed && (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-disabled">·</span>
              Обновлено {article.last_reviewed}
            </span>
          )}
          {platform && (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-disabled">·</span>
              <PlatformIcon art={article} />
              {platform}
            </span>
          )}
        </div>
      </header>

      {/* Body — secure Markdown (D2-A12) */}
      <div className="docs-body">
        <DocsMarkdown
          content={article.body}
          baseSlug={article.slug}
          onNavigate={onNavigate}
          articleExists={articleExists}
        />
      </div>

      {/* Related articles (D2-A6; graceful when empty) */}
      {related.length > 0 && (
        <section aria-label="Связанные статьи" className="mt-10 pt-6 border-t border-hairline">
          <h2 className="text-sm font-semibold text-secondary mb-3">Связанные статьи</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {related.map((r) => (
              <li key={r.slug}>
                <button
                  onClick={() => onNavigate(r.slug)}
                  className="group w-full text-left flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-surface border border-hairline hover:border-strongline transition-colors"
                >
                  <BookOpen size={15} className="text-accent shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-primary group-hover:text-accent transition-colors truncate">
                      {r.title}
                    </span>
                    <span className="block text-[11px] text-tertiary truncate">{sectionLabel(r.section)}</span>
                  </span>
                  <ChevronRight size={15} className="text-disabled shrink-0" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
